import { supabase } from '../lib/supabase';

export const renewalRequestService = {
  // ============================================================================
  // RENEWAL REQUESTS
  // ============================================================================

  async getAllRenewalRequests(filters = {}) {
    try {
      let query = supabase
        ?.from('document_renewal_requests')
        ?.select(`
          *,
          employees (
            employee_number,
            first_name,
            last_name,
            email,
            department,
            position
          )
        `)
        ?.order('created_at', { ascending: false });

      if (filters?.requestStatus) {
        query = query?.eq('request_status', filters?.requestStatus);
      }

      if (filters?.escalationStatus) {
        query = query?.eq('escalation_status', filters?.escalationStatus);
      }

      if (filters?.isOverdue !== undefined) {
        query = query?.eq('is_overdue', filters?.isOverdue);
      }

      if (filters?.documentType) {
        query = query?.eq('document_type', filters?.documentType);
      }

      const { data, error } = await query;

      if (error) throw error;

      return data?.map(row => ({
        id: row?.id,
        documentId: row?.document_id,
        employeeId: row?.employee_id,
        employeeNumber: row?.employees?.employee_number,
        employeeName: `${row?.employees?.first_name} ${row?.employees?.last_name}`,
        employeeEmail: row?.employees?.email,
        department: row?.employees?.department,
        position: row?.employees?.position,
        documentType: row?.document_type,
        documentName: row?.document_name,
        currentExpiryDate: row?.current_expiry_date,
        daysUntilExpiry: row?.days_until_expiry,
        requestStatus: row?.request_status,
        requestPriority: row?.request_priority,
        requestReason: row?.request_reason,
        notificationSent: row?.notification_sent,
        notificationSentAt: row?.notification_sent_at,
        notificationCount: row?.notification_count,
        lastNotificationAt: row?.last_notification_at,
        acknowledgedAt: row?.acknowledged_at,
        submissionStartedAt: row?.submission_started_at,
        submissionCompletedAt: row?.submission_completed_at,
        submissionReferenceNumber: row?.submission_reference_number,
        submissionNotes: row?.submission_notes,
        newDocumentNumber: row?.new_document_number,
        newExpiryDate: row?.new_expiry_date,
        newDocumentUploaded: row?.new_document_uploaded,
        escalationStatus: row?.escalation_status,
        escalationLevel: row?.escalation_level,
        escalationHistory: row?.escalation_history,
        managerNotified: row?.manager_notified,
        managerNotifiedAt: row?.manager_notified_at,
        managerEmail: row?.manager_email,
        departmentHeadNotified: row?.department_head_notified,
        departmentHeadNotifiedAt: row?.department_head_notified_at,
        hrNotified: row?.hr_notified,
        hrNotifiedAt: row?.hr_notified_at,
        responseDeadline: row?.response_deadline,
        submissionDeadline: row?.submission_deadline,
        isOverdue: row?.is_overdue,
        overdueDays: row?.overdue_days,
        complianceStatus: row?.compliance_status,
        complianceRiskLevel: row?.compliance_risk_level,
        metadata: row?.metadata,
        createdAt: row?.created_at,
        updatedAt: row?.updated_at,
        completedAt: row?.completed_at
      })) || [];
    } catch (error) {
      console.error('Error getting renewal requests:', error);
      throw error;
    }
  },

  async getRenewalRequestById(requestId) {
    try {
      const { data, error } = await supabase
        ?.from('document_renewal_requests')
        ?.select(`
          *,
          employees (
            employee_number,
            first_name,
            last_name,
            email,
            department,
            position
          )
        `)
        ?.eq('id', requestId)
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        documentId: data?.document_id,
        employeeId: data?.employee_id,
        employeeNumber: data?.employees?.employee_number,
        employeeName: `${data?.employees?.first_name} ${data?.employees?.last_name}`,
        employeeEmail: data?.employees?.email,
        department: data?.employees?.department,
        position: data?.employees?.position,
        documentType: data?.document_type,
        documentName: data?.document_name,
        currentExpiryDate: data?.current_expiry_date,
        daysUntilExpiry: data?.days_until_expiry,
        requestStatus: data?.request_status,
        requestPriority: data?.request_priority,
        requestReason: data?.request_reason,
        notificationSent: data?.notification_sent,
        notificationSentAt: data?.notification_sent_at,
        notificationCount: data?.notification_count,
        lastNotificationAt: data?.last_notification_at,
        acknowledgedAt: data?.acknowledged_at,
        submissionStartedAt: data?.submission_started_at,
        submissionCompletedAt: data?.submission_completed_at,
        submissionReferenceNumber: data?.submission_reference_number,
        submissionNotes: data?.submission_notes,
        submissionDocuments: data?.submission_documents,
        newDocumentNumber: data?.new_document_number,
        newExpiryDate: data?.new_expiry_date,
        newDocumentUploaded: data?.new_document_uploaded,
        escalationStatus: data?.escalation_status,
        escalationLevel: data?.escalation_level,
        escalationHistory: data?.escalation_history,
        managerNotified: data?.manager_notified,
        managerNotifiedAt: data?.manager_notified_at,
        managerEmail: data?.manager_email,
        managerResponse: data?.manager_response,
        managerResponseAt: data?.manager_response_at,
        departmentHeadNotified: data?.department_head_notified,
        departmentHeadNotifiedAt: data?.department_head_notified_at,
        hrNotified: data?.hr_notified,
        hrNotifiedAt: data?.hr_notified_at,
        hrAssignedTo: data?.hr_assigned_to,
        hrNotes: data?.hr_notes,
        responseDeadline: data?.response_deadline,
        submissionDeadline: data?.submission_deadline,
        isOverdue: data?.is_overdue,
        overdueDays: data?.overdue_days,
        complianceStatus: data?.compliance_status,
        complianceRiskLevel: data?.compliance_risk_level,
        complianceNotes: data?.compliance_notes,
        metadata: data?.metadata,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at,
        completedAt: data?.completed_at
      };
    } catch (error) {
      console.error('Error getting renewal request:', error);
      throw error;
    }
  },

  async updateRenewalRequest(requestId, updates) {
    try {
      const updateData = {};

      if (updates?.requestStatus) updateData.request_status = updates?.requestStatus;
      if (updates?.acknowledgedAt) updateData.acknowledged_at = updates?.acknowledgedAt;
      if (updates?.acknowledgedBy) updateData.acknowledged_by = updates?.acknowledgedBy;
      if (updates?.submissionStartedAt) updateData.submission_started_at = updates?.submissionStartedAt;
      if (updates?.submissionCompletedAt) updateData.submission_completed_at = updates?.submissionCompletedAt;
      if (updates?.submissionReferenceNumber) updateData.submission_reference_number = updates?.submissionReferenceNumber;
      if (updates?.submissionNotes) updateData.submission_notes = updates?.submissionNotes;
      if (updates?.submissionDocuments) updateData.submission_documents = updates?.submissionDocuments;
      if (updates?.newDocumentNumber) updateData.new_document_number = updates?.newDocumentNumber;
      if (updates?.newExpiryDate) updateData.new_expiry_date = updates?.newExpiryDate;
      if (updates?.newDocumentUploaded !== undefined) updateData.new_document_uploaded = updates?.newDocumentUploaded;
      if (updates?.managerResponse) updateData.manager_response = updates?.managerResponse;
      if (updates?.managerResponseAt) updateData.manager_response_at = updates?.managerResponseAt;
      if (updates?.hrNotes) updateData.hr_notes = updates?.hrNotes;
      if (updates?.hrAssignedTo) updateData.hr_assigned_to = updates?.hrAssignedTo;
      if (updates?.complianceNotes) updateData.compliance_notes = updates?.complianceNotes;

      const { data, error } = await supabase
        ?.from('document_renewal_requests')
        ?.update(updateData)
        ?.eq('id', requestId)
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        requestStatus: data?.request_status,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error updating renewal request:', error);
      throw error;
    }
  },

  async acknowledgeRenewalRequest(requestId, acknowledgedBy) {
    try {
      const { data, error } = await supabase
        ?.from('document_renewal_requests')
        ?.update({
          request_status: 'acknowledged',
          acknowledged_at: new Date()?.toISOString(),
          acknowledged_by: acknowledgedBy
        })
        ?.eq('id', requestId)
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        requestStatus: data?.request_status,
        acknowledgedAt: data?.acknowledged_at
      };
    } catch (error) {
      console.error('Error acknowledging renewal request:', error);
      throw error;
    }
  },

  async markSubmissionStarted(requestId) {
    try {
      const { data, error } = await supabase
        ?.from('document_renewal_requests')
        ?.update({
          request_status: 'in_progress',
          submission_started_at: new Date()?.toISOString()
        })
        ?.eq('id', requestId)
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        requestStatus: data?.request_status,
        submissionStartedAt: data?.submission_started_at
      };
    } catch (error) {
      console.error('Error marking submission started:', error);
      throw error;
    }
  },

  async submitRenewalRequest(requestId, submissionData) {
    try {
      const { data, error } = await supabase
        ?.from('document_renewal_requests')
        ?.update({
          request_status: 'submitted',
          submission_completed_at: new Date()?.toISOString(),
          submission_reference_number: submissionData?.referenceNumber,
          submission_notes: submissionData?.notes,
          submission_documents: submissionData?.documents || [],
          compliance_status: 'compliant'
        })
        ?.eq('id', requestId)
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        requestStatus: data?.request_status,
        submissionCompletedAt: data?.submission_completed_at
      };
    } catch (error) {
      console.error('Error submitting renewal request:', error);
      throw error;
    }
  },

  // ============================================================================
  // STATISTICS
  // ============================================================================

  async getRenewalRequestStatistics() {
    try {
      const { data, error } = await supabase
        ?.from('document_renewal_requests')
        ?.select('request_status, escalation_status, is_overdue, compliance_risk_level');

      if (error) throw error;

      const stats = {
        total: data?.length || 0,
        pending: data?.filter(r => r?.request_status === 'pending')?.length || 0,
        notified: data?.filter(r => r?.request_status === 'notified')?.length || 0,
        acknowledged: data?.filter(r => r?.request_status === 'acknowledged')?.length || 0,
        inProgress: data?.filter(r => r?.request_status === 'in_progress')?.length || 0,
        submitted: data?.filter(r => r?.request_status === 'submitted')?.length || 0,
        approved: data?.filter(r => r?.request_status === 'approved')?.length || 0,
        overdue: data?.filter(r => r?.is_overdue === true)?.length || 0,
        escalated: data?.filter(r => r?.escalation_status !== 'none')?.length || 0,
        managerEscalated: data?.filter(r => r?.escalation_status === 'manager_notified')?.length || 0,
        criticalRisk: data?.filter(r => r?.compliance_risk_level === 'critical')?.length || 0,
        highRisk: data?.filter(r => r?.compliance_risk_level === 'high')?.length || 0
      };

      return stats;
    } catch (error) {
      console.error('Error getting renewal request statistics:', error);
      throw error;
    }
  },

  // ============================================================================
  // NOTIFICATIONS
  // ============================================================================

  async getRenewalNotifications(requestId) {
    try {
      const { data, error } = await supabase
        ?.from('renewal_request_notifications')
        ?.select('*')
        ?.eq('renewal_request_id', requestId)
        ?.order('created_at', { ascending: false });

      if (error) throw error;

      return data?.map(row => ({
        id: row?.id,
        renewalRequestId: row?.renewal_request_id,
        notificationType: row?.notification_type,
        recipientType: row?.recipient_type,
        recipientEmail: row?.recipient_email,
        recipientName: row?.recipient_name,
        recipientRole: row?.recipient_role,
        subject: row?.subject,
        message: row?.message,
        emailTemplate: row?.email_template,
        notificationStatus: row?.notification_status,
        scheduledFor: row?.scheduled_for,
        sentAt: row?.sent_at,
        deliveredAt: row?.delivered_at,
        openedAt: row?.opened_at,
        responseReceived: row?.response_received,
        responseAt: row?.response_at,
        errorMessage: row?.error_message,
        retryCount: row?.retry_count,
        metadata: row?.metadata,
        createdAt: row?.created_at
      })) || [];
    } catch (error) {
      console.error('Error getting renewal notifications:', error);
      throw error;
    }
  },

  async getPendingNotifications() {
    try {
      const { data, error } = await supabase
        ?.from('renewal_request_notifications')
        ?.select('*')
        ?.eq('notification_status', 'pending')
        ?.lte('scheduled_for', new Date()?.toISOString())
        ?.order('created_at', { ascending: true });

      if (error) throw error;

      return data?.map(row => ({
        id: row?.id,
        renewalRequestId: row?.renewal_request_id,
        notificationType: row?.notification_type,
        recipientEmail: row?.recipient_email,
        recipientName: row?.recipient_name,
        subject: row?.subject,
        message: row?.message,
        metadata: row?.metadata
      })) || [];
    } catch (error) {
      console.error('Error getting pending notifications:', error);
      throw error;
    }
  },

  async sendRenewalNotificationEmail(notificationId, notificationData) {
    try {
      // Call Edge Function to send email
      const { data: edgeData, error: edgeError } = await supabase?.functions?.invoke(
        'send-document-expiration-alert',
        {
          body: {
            documentId: notificationData?.documentId,
            employeeName: notificationData?.employeeName,
            employeeEmail: notificationData?.employeeEmail,
            documentType: notificationData?.documentType,
            documentName: notificationData?.documentName,
            expiryDate: notificationData?.expiryDate,
            daysUntilExpiry: notificationData?.daysUntilExpiry,
            department: notificationData?.department
          }
        }
      );

      if (edgeError) throw edgeError;

      // Update notification status
      const { error: updateError } = await supabase
        ?.from('renewal_request_notifications')
        ?.update({
          notification_status: 'sent',
          sent_at: new Date()?.toISOString()
        })
        ?.eq('id', notificationId);

      if (updateError) throw updateError;

      return { success: true, emailId: edgeData?.emailId };
    } catch (error) {
      console.error('Error sending renewal notification email:', error);
      
      // Update notification with error
      await supabase
        ?.from('renewal_request_notifications')
        ?.update({
          notification_status: 'failed',
          error_message: error?.message,
          retry_count: supabase?.raw('retry_count + 1')
        })
        ?.eq('id', notificationId);
      
      throw error;
    }
  },

  // ============================================================================
  // NON-COMPLIANCE CASES
  // ============================================================================

  async getNonComplianceCases(filters = {}) {
    try {
      let query = supabase
        ?.from('renewal_non_compliance_cases')
        ?.select(`
          *,
          employees (
            employee_number,
            first_name,
            last_name,
            email,
            department
          )
        `)
        ?.order('created_at', { ascending: false });

      if (filters?.caseStatus) {
        query = query?.eq('case_status', filters?.caseStatus);
      }

      if (filters?.caseSeverity) {
        query = query?.eq('case_severity', filters?.caseSeverity);
      }

      const { data, error } = await query;

      if (error) throw error;

      return data?.map(row => ({
        id: row?.id,
        renewalRequestId: row?.renewal_request_id,
        employeeId: row?.employee_id,
        employeeNumber: row?.employees?.employee_number,
        employeeName: `${row?.employees?.first_name} ${row?.employees?.last_name}`,
        employeeEmail: row?.employees?.email,
        department: row?.employees?.department,
        caseNumber: row?.case_number,
        caseStatus: row?.case_status,
        caseSeverity: row?.case_severity,
        caseType: row?.case_type,
        violationDate: row?.violation_date,
        violationDescription: row?.violation_description,
        daysOverdue: row?.days_overdue,
        actionsTaken: row?.actions_taken,
        escalationPath: row?.escalation_path,
        resolved: row?.resolved,
        resolvedAt: row?.resolved_at,
        resolvedBy: row?.resolved_by,
        resolutionNotes: row?.resolution_notes,
        businessImpact: row?.business_impact,
        legalRisk: row?.legal_risk,
        mitigationPlan: row?.mitigation_plan,
        metadata: row?.metadata,
        createdAt: row?.created_at,
        updatedAt: row?.updated_at
      })) || [];
    } catch (error) {
      console.error('Error getting non-compliance cases:', error);
      throw error;
    }
  },

  async resolveNonComplianceCase(caseId, resolutionData) {
    try {
      const { data, error } = await supabase
        ?.from('renewal_non_compliance_cases')
        ?.update({
          case_status: 'resolved',
          resolved: true,
          resolved_at: new Date()?.toISOString(),
          resolved_by: resolutionData?.resolvedBy,
          resolution_notes: resolutionData?.notes
        })
        ?.eq('id', caseId)
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        caseStatus: data?.case_status,
        resolved: data?.resolved,
        resolvedAt: data?.resolved_at
      };
    } catch (error) {
      console.error('Error resolving non-compliance case:', error);
      throw error;
    }
  },

  // ============================================================================
  // AUTOMATION FUNCTIONS
  // ============================================================================

  async triggerAutoGenerateRequests() {
    try {
      const { error } = await supabase?.rpc('auto_generate_renewal_requests');

      if (error) throw error;

      return { success: true, message: 'Renewal requests generated successfully' };
    } catch (error) {
      console.error('Error triggering auto-generate requests:', error);
      throw error;
    }
  },

  async triggerSendNotifications() {
    try {
      const { error } = await supabase?.rpc('send_renewal_request_notifications');

      if (error) throw error;

      return { success: true, message: 'Notifications sent successfully' };
    } catch (error) {
      console.error('Error triggering send notifications:', error);
      throw error;
    }
  },

  async triggerCheckOverdue() {
    try {
      const { error } = await supabase?.rpc('check_overdue_renewal_requests');

      if (error) throw error;

      return { success: true, message: 'Overdue check completed successfully' };
    } catch (error) {
      console.error('Error triggering overdue check:', error);
      throw error;
    }
  },

  // ============================================================================
  // REAL-TIME SUBSCRIPTIONS
  // ============================================================================

  subscribeToRenewalRequests(callback) {
    const channel = supabase
      ?.channel('renewal_requests_changes')
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'document_renewal_requests'
        },
        (payload) => {
          console.log('Renewal request change:', payload);
          callback?.(payload);
        }
      )
      ?.subscribe();

    return channel;
  },

  unsubscribeFromRenewalRequests(channel) {
    if (channel) {
      supabase?.removeChannel(channel);
    }
  },

  subscribeToNonComplianceCases(callback) {
    const channel = supabase
      ?.channel('non_compliance_cases_changes')
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'renewal_non_compliance_cases'
        },
        (payload) => {
          console.log('Non-compliance case change:', payload);
          callback?.(payload);
        }
      )
      ?.subscribe();

    return channel;
  },

  unsubscribeFromNonComplianceCases(channel) {
    if (channel) {
      supabase?.removeChannel(channel);
    }
  }
};

export default renewalRequestService;